// ingest.text-normalize task (§3.3/§pass2). The cheap model converts one source
// chunk directly into structured records. Source spans are assigned locally at
// chunk granularity; the model never supplies or guesses them.

import { CliError, EXIT_RUNTIME } from "../../../common.js";
import type { Chunk } from "../../../domain/ingest/chunker.js";
import { JsonlParseError, parseJsonl } from "../../../domain/ingest/jsonl-parser.js";
import type { IngestRecord, SourceSpan } from "../../../domain/ingest/records.js";
import type { LlmTaskExecutor } from "../../types.js";

export const TEXT_NORMALIZE_SYSTEM = [
  "你是剧本规范化器。把给定的源文本块转换成结构化剧本记录。",
  "最高优先级是正文零丢失：原文中的每一条动作、环境描写、对白、内心独白、画外音、字幕和转场都必须完整进入 records。禁止因内容重复、次要、过渡性强、篇幅长、使用外语或难以分类而省略。",
  "逐行处理原文，并在输出前逐行自检正文是否全部承载。一个原文段落含多句时必须保留全部句子；可以拆成多条记录，但禁止截断、摘要、合并后漏句或只保留段落前半段。动作翻译只能改变表达语言，不能减少事实。不要输出自检过程。",
  "这是供中文编剧复核的标准稿：dialogue 和 inner_thought 的 content 逐字保留源文本实际语言与夹杂语言，绝不翻译；除此以外，action、transition、note、emotion 以及场景的通用地点描述都使用自然、准确的简体中文。人物姓名在 speaker.name、scene.actors、action 和后续资产登记中都必须沿用源文本的原始拼写，不翻译、不音译、不改成中文名；品牌和没有通行译名的其他专名保留原文。不得把整句外语动作或环境描述原样留在 action 中。",
  "只输出一个标准 JSON 对象，格式必须是 {\"records\":[...]}。必须使用双引号，不要 Markdown、代码围栏、JSONL、注释或解释。",
  "records 按源文本发生顺序排列，只放源文本中实际存在的内容；不要臆造边界，不要提供 source 字段。",
  "",
  "严格保持源文本顺序，绝不重排：块开头若在任何『集/场』标记之前就有正文（上一块没写完的戏），",
  "必须把这些正文原位放在 records 最前面；episode 只在源文本明确集边界处写入。标准剧本沿用原有场界；小说和非标准文本依据地点切换、明显跳时、叙事线切换、明确转场或持久视觉状态变化建立 scene。",
  "绝不要把靠后出现的集边界提到开头。",
  "场号（如 `场3-2`）绝不是集边界；只有原文明确写出 `第N集` 或 `N集` 时才能输出 episode。",
  "",
  "记录类型：",
  "- 集边界：{\"kind\":\"episode\",\"episode\":19,\"title\":\"可选标题\"}。episode 必须取源文本写明的集号；书名、括号、标题文字都不能混进 episode。",
  "- 场边界：{\"kind\":\"scene\",\"environment\":{\"space\":\"interior\",\"time\":\"day\"},\"location\":{\"name\":\"客厅\"},\"actors\":[],\"props\":[]}。不要输出 scene 编号字段，系统会自行连续编号。每个 scene 都必须填写 environment.time，只能使用 day|night|dawn|dusk；依次对应日/夜/晨/昏。",
  "- 动作/环境：{\"kind\":\"action\",\"content\":\"忠实转换成简体中文的可拍摄描述\"}。",
  "- 对白：{\"kind\":\"dialogue\",\"speaker\":{\"name\":\"甲\"},\"content\":\"原话\",\"emotion\":\"可选\"}。",
  "- 内心独白/画外音：{\"kind\":\"inner_thought\",\"speaker\":{\"name\":\"甲\"},\"content\":\"原话\",\"emotion\":\"可选\"}。",
  "- 转场：{\"kind\":\"transition\",\"content\":\"忠实转换成简体中文的转场文字\"}。常见格式词也要翻译，例如 `FLASHBACK` → `闪回`、`BACK TO PRESENT` → `回到现实`。",
  "- 批注：{\"kind\":\"note\",\"content\":\"忠实转换成简体中文的批注\",\"category\":\"author_note|format_note|unclassified\"}。",
  "action、dialogue、inner_thought、transition、note 的 content 必须是非空字符串；不得输出 content 为空的占位记录。原文没有对应正文时就不要创建该记录。",
  "",
  "转换规则：",
  "  源 `场3-2 日/内 客厅` → 一个 scene；space 只用 interior/exterior，time 只用 day/night/dawn/dusk。白天/日间归 day，夜晚/夜间归 night，清晨/黎明归 dawn，黄昏/傍晚归 dusk。原文未直接写明时，根据照明、作息、天空和上下文判断；不得省略或写空串。",
  "  源 `人物：甲【白衣,昏迷】，乙` → scene.actors=[{\"name\":\"甲\",\"state\":\"白衣,昏迷\"},{\"name\":\"乙\"}]。",
  "  源 `道具：婚书，玉佩` → scene.props=[{\"name\":\"婚书\"},{\"name\":\"玉佩\"}]。",
  "  源 `△ 甲推门而入。` → action，content 去掉格式前缀 `△`。",
  "  源 `甲：你来了` → dialogue。源 `甲（冷笑）：你来了` → dialogue.emotion 为 `冷笑`。",
  "  源 `甲OS：（恐慌）他没死吧？` → inner_thought.emotion 为 `恐慌`。",
  "  源 `【字幕：回门日】` → action；画面文字属于画面内容。",
  "  葡语动作 `Richard avança, agarra Emily pela roupa e a puxa para cima.` → action.content=`Richard上前抓住Emily的衣服，把她拽了起来。`。",
  "  葡语动作 `Um prato de oferendas cai da mesa e se espalha pelo chão.` → action.content=`一盘供品从桌上掉落，散落一地。`。人物说出的葡语对白仍逐字保留，不翻译；Richard、Emily 等人物名始终保持原文拼写。",
  "  同一句由多人同时说时，为每个说话人各写一条相邻 dialogue，content 相同。",
  "",
  "每种记录只能使用上面列出的字段，禁止增加 name、text、line、episode_title 等自创字段。",
  "正文口径：dialogue、inner_thought 的 content 逐字完整保留，不润色、不扩写、不纠错、不翻译。action、transition、note 和 emotion 忠实转换成简体中文；翻译不得改变、删减或补造原文事实、因果、顺序、动作结果、人物状态以及已有的机位、景别和镜头运动。小说叙述转换为可直接拍摄的 action 时遵守同一边界。人物/道具的在场只写在 scene 的 actors/props。",
  "action 直接写具体人物名，明确行动者、动作、作用对象、空间方向和可见结果；多个先后发生的动作、反应、接触、道具变化或镜头变化按原文顺序分别承载，不压成剧情概述。身份明确时消解代词，无法确认时保持可核查描述，不猜测。只用原文明确写出的表情、视线、肢体、道具或环境反应表达状态，不用情绪、态度或心理判断替代可见事实。",
  "location 是可独立布置和重复引用的稳定物理空间；人物跨门槛进入另一空间、镜头切到另一空间、明显跳时、切换叙事线或原文明确转场时建立 scene，一个 scene 只写一个主 location。同一连续场内的人物进出、情绪升级、攻防回合、换装、脱衣、地点受损或道具形态变化本身不构成新 scene；把变化过程写入 action，后续资产治理会维护持久 state。",
  "同地点连续蒙太奇保留一个 scene 并用 transition；跨地点蒙太奇按剪辑顺序拆成多个 scene，每场填写自己的 location、actors、props 和 state。",
  "人物只列具体单人角色；家族/机构/群体/泛称不列入 actors。群体喊话的 speaker 用 {\"kind\":\"group\"}。",
  "非人物发声源的 speaker.kind 只用 system|broadcast|offscreen|group，可用 label 补充名称；具体人物始终用 speaker.name。",
  "speaker.name 必须填写原文中能确认的具体人物名字并保持原始拼写；scene.actors 与 action 中再次出现该人物时使用同一拼写。禁止翻译、音译、中文化人物名，也禁止用『？』『？？』『未知』『unknown』等占位名；身份无法确认时改用 speaker.kind=offscreen，群体共同发声用 group。",
  "每条 dialogue 只在原文明确给出时，才把发声方式、声音来源或不改变连续性的短促伴随动作转换成简体中文 emotion；影响走位、姿势、接触、道具或连续性的动作单独写 action。原文没有表演或声音证据时省略 emotion，不按语境补造。",
  "note 只用于真正无法归入剧本、也不属于画面/对白的内容。源文本块的实质内容不得丢失。",
].join("\n");

export function buildNormalizeUser(chunk: Chunk): string {
  // Do NOT expose the chunk index — the model conflates a chunk number with an
  // episode number. Chunk source spans are assigned by the parser, not the model.
  const position = chunk.index === 0
    ? "这是源文件开头；小说或非标准文本从第一个可确认的物理空间建立首场。"
    : "这个片段可能承接上一片段的同一场；只有源文本出现真实场界时才建立新场。";
  return [
    "【待规范化的原文片段】",
    position,
    "以下正文必须逐行、逐句完整进入 records：对白和内心独白逐字保留原语言；动作、环境、转场、批注和表演提示忠实转换成简体中文；人物名在所有记录中保持原文拼写，不翻译、不音译。不得遗漏、摘要、截断或补造。输出前请自行核对每一行，且不要输出核对过程。",
    "",
    chunk.text,
  ].join("\n");
}

function runtimeError(message: string, received: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_RUNTIME,
    errorCode: "INGEST_NORMALIZE_FAILED",
    hint: "Inspect the chunk error sidecar and rerun `scriptctl ingest`.",
    data: { actual: received },
  });
}

type NormalizeOutput = { records: unknown[] };

function isNormalizeOutput(input: unknown): input is NormalizeOutput {
  if (typeof input !== "object" || input === null || Array.isArray(input)) return false;
  return Object.keys(input).length === 1 && "records" in input && Array.isArray(input.records);
}

// Validate provider output with the same strict parser used by the merged JSONL.
// The model owns record semantics; the pipeline owns the trusted source span.
export function parseNormalizeOutput(input: unknown, chunk: Chunk): IngestRecord[] {
  if (!isNormalizeOutput(input)) {
    throw runtimeError(`chunk ${chunk.index}: output must be an object containing only records[]`, ["invalid structured output"]);
  }
  const source: SourceSpan = { chunk: chunk.index, line_start: chunk.core_line_start, line_end: chunk.core_line_end };
  let records: IngestRecord[];
  try {
    const jsonl = input.records.map((record) => {
      if (typeof record !== "object" || record === null || Array.isArray(record) || Object.hasOwn(record, "source")) {
        throw new JsonlParseError("record must be an object without source", 1);
      }
      return JSON.stringify({ ...record, source });
    }).join("\n");
    records = parseJsonl(jsonl);
  } catch (error) {
    if (error instanceof JsonlParseError) throw runtimeError(`chunk ${chunk.index}: ${error.message}`, [error.message]);
    throw error;
  }
  if (records.length === 0 && chunk.text.trim() !== "") {
    throw runtimeError(`chunk ${chunk.index}: non-empty chunk produced no records`, ["records: 0"]);
  }
  return records;
}

export async function runTextNormalize(
  executor: LlmTaskExecutor,
  chunk: Chunk,
  signal?: AbortSignal,
): Promise<{ raw: string; records: IngestRecord[] }> {
  const response = await executor.generateText("ingest.text-normalize", {
    system: TEXT_NORMALIZE_SYSTEM,
    user: buildNormalizeUser(chunk),
    responseFormat: "json_object",
    ...(signal ? { signal } : {}),
  });
  let input: unknown;
  try {
    input = JSON.parse(response.text) as unknown;
  } catch {
    throw runtimeError(`chunk ${chunk.index}: provider returned invalid JSON`, [response.text.slice(0, 500)]);
  }
  return { raw: response.text, records: parseNormalizeOutput(input, chunk) };
}
